// app/app/services/logger.ts
import pino from "pino";
import { config } from "../config";

type LogLevel = 'debug' | 'info' | 'warn' | 'error';
type Logger = pino.Logger & Record<LogLevel, (obj: object, msg?: string) => void>;

// Environment detection
const isDev = process.env.NODE_ENV === "development";
const isTest = process.env.NODE_ENV === "test";

// Safe stringify helper (guards against circular refs)
function safeStringify(value: unknown, maxLen?: number): string {
  try {
    const s = JSON.stringify(value);
    return typeof maxLen === "number" ? s.substring(0, maxLen) : s;
  } catch {
    // Fallback for non-serializable values
    try {
      return String(value);
    } catch {
      return "[unserializable]";
    }
  }
}

// Get log level from config (defaults to 'warn' in production)
const logLevel = isTest ? "silent" : (config.app.logLevel || "warn");

// Create base logger configuration
const logger = pino({
  name: "stash-web",
  level: logLevel,

  // Browser configuration
  browser: {
    serialize: true,
    formatters: {
      level: (label, _number) => ({ level: label }),
      log: (object) => object,
    },
  },

  // Server/Development configuration
  ...(typeof window === "undefined" && {
    transport: isDev
      ? {
          target: "pino-pretty",
          options: {
            colorize: true,
            ignore: "pid,hostname",
            translateTime: "SYS:standard",
            singleLine: true,
          },
        }
      : undefined,

    formatters: {
      level: (label: string) => ({ level: label }),
    },

    timestamp: pino.stdTimeFunctions.isoTime,

    // Add request ID correlation
    mixin() {
      return {
        environment: process.env.NODE_ENV || "unknown",
        service: "stash-web",
      };
    },
  }),
});

// Utility functions for common logging patterns
export const loggerUtils = {
  /**
   * Log API request start
   */
  apiRequest: (method: string, url: string, data?: unknown) => {
    logger.info(
      {
        type: "api_request",
        method,
        url,
        data: data ? safeStringify(data, 500) : undefined,
      },
      `API Request: ${method} ${url}`,
    );
  },

  /**
   * Log API response
   */
  apiResponse: (
    method: string,
    url: string,
    status: number,
    duration?: number,
    data?: unknown,
  ) => {
    const level = status >= 400 ? "error" : status >= 300 ? "warn" : "info";
    const dataStr = data ? safeStringify(data) : undefined;

    (logger as Logger)[level](
      {
        type: "api_response",
        method,
        url,
        status,
        duration,
        dataSize: dataStr ? dataStr.length : undefined,
      },
      `API Response: ${method} ${url} - ${status} ${duration ? `(${duration}ms)` : ""}`,
    );
  },

  /**
   * Log user actions
   */
  userAction: (action: string, details?: Record<string, unknown>) => {
    logger.info(
      {
        type: "user_action",
        action,
        ...(details ?? {}),
        timestamp: new Date().toISOString(),
      },
      `User Action: ${action}`,
    );
  },

  /**
   * Log navigation/route changes
   */
  navigation: (from: string, to: string, params?: Record<string, unknown>) => {
    logger.info(
      {
        type: "navigation",
        from,
        to,
        ...(params ?? {}),
      },
      `Navigation: ${from} → ${to}`,
    );
  },

  /**
   * Log performance metrics
   */
  performance: (
    metric: string,
    value: number,
    unit = "ms",
    metadata?: Record<string, unknown>,
  ) => {
    logger.info(
      {
        type: "performance",
        metric,
        value,
        unit,
        ...(metadata ?? {}),
      },
      `Performance: ${metric} = ${value}${unit}`,
    );
  },

  /**
   * Log component lifecycle events
   */
  component: (
    name: string,
    event: "mount" | "unmount" | "render" | "error",
    details?: Record<string, unknown>,
  ) => {
    const level = event === "error" ? "error" : "debug";
    (logger as Logger)[level](
      {
        type: "component",
        component: name,
        event,
        ...(details ?? {}),
      },
      `Component ${name}: ${event}`,
    );
  },

  /**
   * Log file operations
   */
  fileOperation: (
    operation: string,
    fileName?: string,
    size?: number,
    details?: Record<string, unknown>,
  ) => {
    logger.info(
      {
        type: "file_operation",
        operation,
        fileName,
        size,
        ...(details ?? {}),
      },
      `File Operation: ${operation} ${fileName ? `- ${fileName}` : ""} ${
        size ? `(${(size / 1024 / 1024).toFixed(2)}MB)` : ""
      }`,
    );
  },

  /**
   * Log authentication events
   */
  auth: (
    event: "login" | "logout" | "token_refresh" | "auth_error" | "login_success" | "forced_logout" | "csrf_token_refreshed" | "user_login",
    details?: Record<string, unknown>,
  ) => {
    const level = event === "auth_error" ? "error" : "info";
    (logger as Logger)[level](
      {
        type: "auth",
        event,
        ...(details ?? {}),
      },
      `Auth: ${event}`,
    );
  },

  /**
   * Log store/state changes
   */
  state: (store: string, action: string, data?: unknown) => {
    logger.debug(
      {
        type: "state_change",
        store,
        action,
        data: data ? safeStringify(data, 200) : undefined,
      },
      `State: ${store}.${action}`,
    );
  },

  /**
   * Log upload progress and events
   */
  upload: (
    event: "start" | "progress" | "success" | "error" | "cancel" | "resume",
    fileName?: string,
    progress?: number,
    details?: Record<string, unknown>,
  ) => {
    const level = event === "error" ? "error" : "info";
    (logger as Logger)[level](
      {
        type: "upload",
        event,
        fileName,
        progress,
        ...(details ?? {}),
      },
      `Upload: ${fileName ? `${fileName} - ` : ""}${event} ${
        progress !== undefined ? `(${progress}%)` : ""
      }`,
    );
  },

  /**
   * Log errors with stack traces and context
   */
  error: (
    error: Error | string,
    context?: string,
    metadata?: Record<string, unknown>,
  ) => {
    if (typeof error === "string") {
      logger.error(
        {
          type: "error",
          context,
          ...(metadata ?? {}),
        },
        error,
      );
    } else {
      logger.error(
        {
          type: "error",
          context,
          error: {
            name: error.name,
            message: error.message,
            stack: error.stack,
          },
          ...(metadata ?? {}),
        },
        `Error in ${context || "unknown"}: ${error.message}`,
      );
    }
  },
};

// Export the main logger and utilities
export { logger };
export default logger;

// Browser-specific enhancements
if (typeof window !== "undefined") {
  // Log unhandled errors
  window.addEventListener("error", (event: ErrorEvent) => {
    loggerUtils.error(
      event.error ?? new Error(event.message),
      "unhandled_error",
      {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      },
    );
  });

  // Log unhandled promise rejections
  window.addEventListener(
    "unhandledrejection",
    (event: PromiseRejectionEvent) => {
      const reason = event.reason;
      const err = reason instanceof Error ? reason : new Error(String(reason));
      loggerUtils.error(err, "unhandled_rejection", {
        reason:
          typeof reason === "object" ? safeStringify(reason) : String(reason),
      });
    },
  );

  // Log page visibility changes
  document.addEventListener("visibilitychange", () => {
    loggerUtils.userAction("page_visibility_change", {
      visible: !document.hidden,
    });
  });

  // Performance observer for web vitals
  if ("PerformanceObserver" in window) {
    try {
      const observer = new PerformanceObserver((list) => {
        list.getEntries().forEach((entry) => {
          if (entry.entryType === "navigation") {
            const navEntry = entry as PerformanceNavigationTiming;
            loggerUtils.performance(
              "page_load_time",
              Math.round(navEntry.loadEventEnd - navEntry.fetchStart),
              "ms",
              {
                dns_time: Math.round(
                  navEntry.domainLookupEnd - navEntry.domainLookupStart,
                ),
                connect_time: Math.round(
                  navEntry.connectEnd - navEntry.connectStart,
                ),
                request_time: Math.round(
                  navEntry.responseEnd - navEntry.requestStart,
                ),
                dom_processing: Math.round(
                  navEntry.domComplete - navEntry.domInteractive,
                ),
              },
            );
          }
        });
      });

      observer.observe({ entryTypes: ["navigation"] });
    } catch (_e) {
      // Performance observer not supported
    }
  }
}
